/**
 * Utility functions for creating and parsing company slugs with hashes
 */

/**
 * Generate a simple hash from a string
 * @param {string} str - String to hash
 * @returns {string} - Short hash (6 characters)
 */
const generateShortHash = (str) => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  return Math.abs(hash).toString(36).substring(0, 6);
};

/**
 * Create a URL-friendly slug from text
 * @param {string} text - Text to slugify
 * @returns {string} - URL-friendly slug
 */
const slugify = (text) => {
  return text
    .toLowerCase()
    .replace(/[^\w\s-]/g, '') // Remove special characters
    .replace(/[\s_-]+/g, '-') // Replace spaces and underscores with hyphens
    .replace(/^-+|-+$/g, ''); // Remove leading/trailing hyphens
};

/**
 * Create a company slug with hash from employer data
 * @param {Object} employer - Employer object with company_name/full_name and id
 * @returns {string} - Company slug with hash (e.g., "tech-solutions-inc-a1b2c3")
 */
export const createCompanySlug = (employer) => {
  if (!employer) return '';
  
  const companyName = employer.company_name || employer.full_name || 'company';
  const slug = slugify(companyName);
  const hash = generateShortHash(employer.id || '');
  
  return `${slug}-${hash}`;
};

/**
 * Extract company name from a company slug
 * @param {string} companySlug - Company slug with hash
 * @returns {string} - Company name (without hash)
 */
export const extractCompanyNameFromSlug = (companySlug) => {
  if (!companySlug) return '';
  
  // Split by hyphen and remove the last part (hash)
  const parts = companySlug.split('-');
  if (parts.length <= 1) return companySlug;
  
  // Remove the last part (hash) and rejoin
  const nameparts = parts.slice(0, -1);
  return nameparts.join('-').replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
};

/**
 * Extract hash from a company slug
 * @param {string} companySlug - Company slug with hash
 * @returns {string} - Hash part of the slug
 */
export const extractHashFromSlug = (companySlug) => {
  if (!companySlug) return '';
  
  const parts = companySlug.split('-');
  return parts.length > 1 ? parts[parts.length - 1] : '';
};

/**
 * Check if an employer matches a company slug
 * @param {Object} employer - Employer object
 * @param {string} companySlug - Company slug to match against
 * @returns {boolean} - True if employer matches the slug
 */
export const employerMatchesSlug = (employer, companySlug) => {
  if (!employer || !companySlug) return false;
  
  const employerSlug = createCompanySlug(employer);
  return employerSlug === companySlug;
};